import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { giftCardAPI } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import LocalizedLink from '../components/LocalizedLink';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';
import { PhoneInput } from '../components/PhoneInput';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
const SESSION_PRICE = Number(process.env.REACT_APP_GIFT_CARD_SESSION_PRICE) || 50;

interface FormData {
  amount: number;
  senderName: string;
  senderEmail: string;
  senderPhone: string;
  recipientName: string;
  recipientEmail: string;
  message: string;
}

const generateGiftCardItemId = () => `gc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const GiftCardPurchaseForm: React.FC = () => {
  const { t, language } = useI18n();
  const { user, isAuthenticated } = useAuth0();
  const navigate = useLocalizedNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [giftCardItemId] = useState(() => generateGiftCardItemId());
  const [amount, setAmount] = useState('50');
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState<Omit<FormData, 'amount'>>({
    senderName: '',
    senderEmail: '',
    senderPhone: '',
    recipientName: '',
    recipientEmail: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData((prev) => ({
        ...prev,
        senderName: user.name || '',
        senderEmail: user.email || '',
      }));
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    clearEcommerce();
    pushToDataLayer('begin_checkout', {
      content: { category: 'gift_card' },
      ecommerce: {
        items: [{
          item_id: giftCardItemId,
          item_name: 'Gift Card',
          item_category: 'Gift Card'
        }]
      }
    });
  }, [giftCardItemId]);

  const hasFiredPaymentInfo = useRef(false);
  const handleInteraction = () => {
    if (!hasFiredPaymentInfo.current) {
      clearEcommerce();
      pushToDataLayer('add_payment_info', {
        content: { category: 'gift_card' },
        ecommerce: {
          payment_type: 'pay_with_card',
          currency: 'EUR',
          items: [{
            item_id: giftCardItemId,
            item_name: 'Gift Card',
            item_category: 'Gift Card',
            value: getAmount()
          }]
        },
        customer: {
          email: formData.senderEmail,
          phone: formData.senderPhone
        }
      });
      hasFiredPaymentInfo.current = true;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getAmount = (): number => {
    return amount === 'other' ? Number(customAmount) : Number(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const card = elements.getElement(CardNumberElement);
      if (!card) {
        throw new Error(t.checkout.cardElementError || 'Card element not found');
      }

      const purchaseAmount = getAmount();

      if (amount === 'other' && (!customAmount || purchaseAmount < 50)) {
        throw new Error(t.giftCard.minimumAmountError || 'Minimum amount is 50 €');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        await getClientSecret(purchaseAmount),
        {
          payment_method: {
            card: card,
            billing_details: {
              name: formData.senderName,
              email: formData.senderEmail,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || t.checkout.paymentError || 'Payment failed');
      }

      if (paymentIntent?.status === 'succeeded') {
        try {
          const purchaseAmount = getAmount();
          const purchaseData = {
            paymentIntentId: paymentIntent.id,
            amount: purchaseAmount,
            senderName: formData.senderName,
            senderEmail: formData.senderEmail,
            recipientName: formData.recipientName,
            recipientEmail: formData.recipientEmail,
            message: formData.message,
            language: language,
          };
          const response = await giftCardAPI.confirmPurchase(purchaseData);
          
          navigate('/gift-card-success', {
            state: { 
              code: response.data.code,
              amount: purchaseAmount,
              senderName: formData.senderName,
              senderEmail: formData.senderEmail,
              senderPhone: formData.senderPhone,
              recipientName: formData.recipientName,
              recipientEmail: formData.recipientEmail,
              paymentIntentId: paymentIntent.id,
              giftCardItemId: giftCardItemId,
            },
          });
        } catch (apiError: any) {
          setErrorMessage((t.errors?.confirmationFailed || 'Confirmation failed') + (apiError.response?.data?.message || ''));
          setIsLoading(false);
        }
      } else {
        setErrorMessage(t.errors?.paymentNotSucceeded || 'Payment was not successful');
        setIsLoading(false);
      }
    } catch (err: any) {
      setErrorMessage(err.message || t.giftCard.errorMessage || 'An error occurred');
      setIsLoading(false);
    }
  };

  const getClientSecret = async (amount: number): Promise<string> => {
    const response = await giftCardAPI.createPaymentIntent(amount);
    return (response.data as any).clientSecret || response.data.paymentIntentId;
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">{t.giftCard.detailsTitle || 'Gift Card Details'}</h2>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-3">
              {t.giftCard.chooseAmount}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {['50', '100', '250'].map((value) => {
                const sessions = Number(value) / SESSION_PRICE;
                return (
                  <button
                    type="button"
                    key={value}
                    onClick={() => {
                      setAmount(value);
                      setCustomAmount('');
                    }}
                    className={`px-4 py-3 rounded-md font-medium transition-colors flex flex-col items-center ${
                      amount === value
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <span className="text-lg font-bold">{formatPrice(Number(value))}</span>
                    <span className={`mt-1 text-xs ${
                      amount === value ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {t.giftCard[sessions === 1 ? 'session' : 'sessions'].replace('{{count}}', sessions.toString())}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setAmount('other')}
                className={`px-4 py-3 rounded-md font-medium transition-colors ${
                  amount === 'other'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t.giftCard.other}
              </button>
            </div>
            {amount === 'other' && (
              <div className="mt-3">
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t.giftCard.enterAmountPlaceholder}
                  min="50"
                  step="0.01"
                  required
                  disabled={isLoading}
                />
              </div>
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">{t.giftCard.from}</h3>
            <div className="mb-4">
              <label htmlFor="senderName" className="block text-gray-700 text-sm font-bold mb-2">
                {t.giftCard.yourName} *
              </label>
              <input
                id="senderName"
                name="senderName"
                type="text"
                value={formData.senderName}
                onChange={handleChange}
                onFocus={handleInteraction}
                required
                className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="senderEmail" className="block text-gray-700 text-sm font-bold mb-2">
                {t.giftCard.yourEmail} *
              </label>
              <input
                id="senderEmail"
                name="senderEmail"
                type="email"
                value={formData.senderEmail}
                onChange={handleChange}
                onFocus={handleInteraction}
                required
                className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t.giftCard.yourPhone || 'Your Phone'} *
              </label>
              <PhoneInput
                value={formData.senderPhone}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, senderPhone: value }));
                  handleInteraction();
                }}
                placeholder="Phone number"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">{t.giftCard.to}</h3>
            <div className="mb-4">
              <label htmlFor="recipientName" className="block text-gray-700 text-sm font-bold mb-2">
                {t.giftCard.recipientName} *
              </label>
              <input
                id="recipientName"
                name="recipientName"
                type="text"
                value={formData.recipientName}
                onChange={handleChange}
                required
                className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t.giftCard.recipientNamePlaceholder}
                disabled={isLoading}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="recipientEmail" className="block text-gray-700 text-sm font-bold mb-2">
                {t.giftCard.deliveryEmail} *
              </label>
              <input
                id="recipientEmail"
                name="recipientEmail"
                type="email"
                value={formData.recipientEmail}
                onChange={handleChange}
                required
                className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t.giftCard.recipientEmailPlaceholder}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <label htmlFor="message" className="block text-gray-700 text-sm font-bold mb-2">
              {t.giftCard.personalMessage}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="shadow-sm border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t.giftCard.personalMessagePlaceholder}
              maxLength={500}
              disabled={isLoading}
            />
            <p className="text-gray-500 text-sm mt-1">
              {formData.message.length}/500 {t.giftCard.characters}
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
            </svg>
            <h2 className="text-xl font-bold">{t.checkout.paymentDetails}</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t.checkout.securePaymentInfo}</p>
          <div className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {t.checkout.cardNumber || 'Card Number'}
              </label>
              <div className="border border-gray-300 rounded-md p-4">
                <CardNumberElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#32325d',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t.checkout.expiryDate || 'Expiry Date'}
                </label>
                <div className="border border-gray-300 rounded-md p-4">
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#32325d',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t.checkout.cvc || 'CVC'}
                </label>
                <div className="border border-gray-300 rounded-md p-4">
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#32325d',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="text-red-600 mt-4" role="alert">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !stripe || !elements}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t.giftCard.processing : t.checkout.payAmount.replace('{{amount}}', formatPrice(getAmount()))}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export const GiftCardPurchasePage: React.FC = () => {
  const { t } = useI18n();

  useDocumentTitle(t.giftCard.purchaseTitle);

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-6xl">
      <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center text-gray-800">
        {t.giftCard.purchaseTitle}
      </h1>
      <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
        {t.giftCard.purchaseDescription}
      </p>
      <Elements stripe={stripePromise}>
        <GiftCardPurchaseForm />
      </Elements>

      <div className="mt-8 text-center border-t border-gray-200 pt-6">
        <p className="text-gray-600">
          {t.giftCard.have}{' '}
          <LocalizedLink
            to="/check-balance"
            className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
          >
            {t.footer.checkGiftCardBalance}
          </LocalizedLink>
        </p>
      </div>
    </div>
  );
};

export default GiftCardPurchasePage;
